import moment from "moment";

export function formatChangeDate(raw) {
  if (raw == null || raw === "") return "";
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format("D MMM YYYY") : String(raw);
}

/** newDirections.departure may be a string or { date }. */
export function getNewDirectionDepartureDate(direction) {
  if (!direction) return "";
  const departure = direction.departure;
  if (typeof departure === "string" && departure.trim()) return departure.trim();
  if (departure && typeof departure === "object") {
    if (departure.date) return departure.date;
  }
  return direction.date || direction.departureDate || "";
}

export function getFlightBookingFromChange(data) {
  const raw = data?.flightBooking ?? data?.originalItinerary;
  if (Array.isArray(raw)) return raw[0] || null;
  if (raw?.flights) return raw;
  return raw || null;
}

/** selectedOffer may arrive as a JSON string on the change request. */
export function parseChangeSelectedOffer(data) {
  const raw = data?.selectedOffer;
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }
  return null;
}

function parseOfferMoney(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value == null || value === "") return null;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

const PAX_TYPE_ORDER = ["adult", "child", "infant"];
const PAX_TYPE_LABEL = {
  adult: "Adult",
  child: "Child",
  infant: "Infant",
};

function normalizePaxTypeKey(key) {
  const raw = String(key || "")
    .trim()
    .toLowerCase();
  if (["adult", "adt", "a"].includes(raw)) return "adult";
  if (["child", "chd", "cnn", "c"].includes(raw)) return "child";
  if (["infant", "inf", "i"].includes(raw)) return "infant";
  return raw || "";
}

function bookingPaxFare(booking, type) {
  const block =
    booking?.[type] ||
    booking?.[type === "adult" ? "Adult" : type === "child" ? "Child" : "Infant"];
  if (!block || typeof block !== "object") return null;
  const count = Number(block.passengerCount || block.count || 0) || 0;
  const total =
    parseOfferMoney(block.flightPayable) ??
    parseOfferMoney(block.totalPrice) ??
    parseOfferMoney(block.payable);
  if (total == null && !count) return null;
  return { count, total };
}

function offerPaxFare(offer, type) {
  const fares = offer?.pricingInformation?.passengerFares;
  if (!fares || typeof fares !== "object") return null;
  const entry =
    Object.entries(fares).find(
      ([key]) => normalizePaxTypeKey(key) === type,
    )?.[1] || null;
  if (!entry || typeof entry !== "object") return null;
  const count = Number(entry.passengerCount || entry.count || 0) || 0;
  const total =
    parseOfferMoney(entry.flightPayable) ??
    parseOfferMoney(entry.totalPrice) ??
    parseOfferMoney(entry.payable);
  if (total == null && !count) return null;
  return { count, total };
}

/**
 * Fare hint for an agent-picked selectedOffer: original ticket total vs
 * the offer's payable, the overall difference, and the same by pax type.
 * Returns null when there is no selected offer.
 */
export function getChangeSelectedOfferFareHint(data) {
  const offer = parseChangeSelectedOffer(data);
  if (!offer) return null;
  const booking = getFlightBookingFromChange(data);
  const offerTotal =
    parseOfferMoney(offer?.pricingInformation?.price?.flightPayable) ??
    parseOfferMoney(offer?.pricingInformation?.price?.totalPrice);
  const oldTotal =
    parseOfferMoney(booking?.grandTotal) ??
    parseOfferMoney(booking?.payable) ??
    parseOfferMoney(booking?.paymentAmount);

  const byPaxType = PAX_TYPE_ORDER.map((type) => {
    const oldPax = bookingPaxFare(booking, type);
    const newPax = offerPaxFare(offer, type);
    if (!oldPax && !newPax) return null;
    const count = newPax?.count || oldPax?.count || 0;
    const oldAmount = oldPax?.total ?? null;
    const offerAmount = newPax?.total ?? null;
    return {
      type,
      label: PAX_TYPE_LABEL[type] || type,
      count,
      oldTotal: oldAmount,
      offerTotal: offerAmount,
      difference:
        offerAmount != null && oldAmount != null
          ? offerAmount - oldAmount
          : null,
      perPassenger:
        offerAmount != null &&
        oldAmount != null &&
        count > 0
          ? (offerAmount - oldAmount) / count
          : null,
    };
  }).filter(Boolean);

  return {
    offer,
    offerTotal,
    oldTotal,
    difference:
      offerTotal != null && oldTotal != null ? offerTotal - oldTotal : null,
    byPaxType,
  };
}

function airportCode(value) {
  if (value == null || value === "") return "";
  return String(value).trim().toUpperCase();
}

/**
 * Same-city / metro airports — destinations often say LHR while the
 * ticketed flights use LGW. That is not a route change.
 */
const METRO_AIRPORT = {
  LHR: "LON",
  LGW: "LON",
  LCY: "LON",
  STN: "LON",
  LTN: "LON",
  CDG: "PAR",
  ORY: "PAR",
  BVA: "PAR",
  EWR: "NYC",
  JFK: "NYC",
  LGA: "NYC",
  ORD: "CHI",
  MDW: "CHI",
  DXB: "DXB",
  DWC: "DXB",
};

function airportRouteToken(value) {
  const code = airportCode(value);
  return METRO_AIRPORT[code] || code;
}

function routePairKey(from, to) {
  return `${airportRouteToken(from)}-${airportRouteToken(to)}`;
}

function formatOd(from, to) {
  const a = airportCode(from);
  const b = airportCode(to);
  if (!a || !b) return "";
  return `${a} - ${b}`;
}

function formatRoutePairs(pairs) {
  return (pairs || [])
    .map((pair) => formatOd(pair?.from, pair?.to))
    .filter(Boolean)
    .join(" / ");
}

function sortSegmentGroups(groups) {
  return groups.slice().sort((a, b) => {
    const sa = Number(a.segment);
    const sb = Number(b.segment);
    if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb;
    return String(a.segment || "").localeCompare(String(b.segment || ""));
  });
}

/**
 * Group connecting legs by journey `segment`.
 * e.g. LOS→IST + IST→LGW (segment 1) → LOS - LGW
 */
export function groupNewDirectionsBySegment(directions) {
  const list = Array.isArray(directions) ? directions : [];
  if (!list.length) return [];
  const groups = [];
  const indexByKey = new Map();

  list.forEach((direction, index) => {
    const segment =
      direction?.segment != null && direction.segment !== ""
        ? String(direction.segment)
        : "";
    // Segment only — do not fall back to journeyType (return legs are
    // sometimes mistagged Outbound and would split the wrong way).
    const key = segment ? `seg:${segment}` : `leg:${index}`;
    let group = groups[indexByKey.get(key)];
    if (!group) {
      group = {
        key,
        segment: segment || String(groups.length + 1),
        journeyType: direction?.journeyType || "",
        legs: [],
      };
      indexByKey.set(key, groups.length);
      groups.push(group);
    }
    group.legs.push(direction);
  });

  return sortSegmentGroups(groups).map((group) => {
    const first = group.legs[0];
    const last = group.legs[group.legs.length - 1];
    const stops = group.legs
      .slice(0, -1)
      .map((leg) => airportCode(leg?.to))
      .filter((code) => code && code !== airportCode(first?.from));
    return {
      segment: group.segment,
      journeyType: group.journeyType || first?.journeyType || "",
      from: airportCode(first?.from),
      to: airportCode(last?.to),
      departure: getNewDirectionDepartureDate(first),
      legs: group.legs,
      stops,
      segmentRefs: group.legs.map((leg) => leg?.segmentRef).filter(Boolean),
    };
  });
}

function odPairsFromFlights(flights) {
  const list = Array.isArray(flights) ? flights : [];
  if (!list.length) return [];
  const bySegment = new Map();
  list.forEach((flight, index) => {
    const segment =
      flight?.segment != null && flight.segment !== ""
        ? String(flight.segment)
        : `leg:${flight?.segmentRef || index}`;
    if (!bySegment.has(segment)) bySegment.set(segment, []);
    bySegment.get(segment).push(flight);
  });
  return [...bySegment.entries()]
    .sort((a, b) => {
      const sa = Number(a[0]);
      const sb = Number(b[0]);
      if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb;
      return String(a[0]).localeCompare(String(b[0]));
    })
    .map(([segment, legs]) => ({
      segment,
      from: airportCode(
        legs[0]?.departureLocation || legs[0]?.departure?.location,
      ),
      to: airportCode(
        legs[legs.length - 1]?.arrivalLocation ||
          legs[legs.length - 1]?.arrival?.location,
      ),
      departureDate:
        legs[0]?.departureDate ||
        legs[0]?.departure?.date ||
        legs[0]?.departureDate ||
        "",
    }));
}

/**
 * Original journey O&D for comparison.
 * Prefer ticketed flights (actual airports) over commercial destinations —
 * destinations may say LHR while flights arrive LGW.
 */
export function getOriginalRoutePairs(data, flightData) {
  const booking =
    flightData ||
    getFlightBookingFromChange(data) ||
    data?.originalItinerary ||
    {};
  const destinations =
    booking?.destinations || data?.originalItinerary?.destinations || [];
  const flights =
    booking?.flights || data?.originalItinerary?.flights || [];
  const fromFlights = odPairsFromFlights(flights);
  if (fromFlights.length) {
    return fromFlights.map((pair, index) => ({
      ...pair,
      departureDate:
        pair.departureDate ||
        destinations[index]?.departureDate ||
        destinations[index]?.date ||
        "",
    }));
  }
  if (destinations.length) {
    return destinations.map((dest, index) => ({
      segment: String(index + 1),
      from: airportCode(dest?.from),
      to: airportCode(dest?.to),
      departureDate: dest?.departureDate || dest?.date || "",
    }));
  }
  return [];
}

/**
 * Requested journey O&D — always prefer newDirections grouped by segment
 * so stops collapse (LOS→IST→LGW → LOS - LGW), not commercial offer labels.
 */
export function getRequestedRoutePairs(data) {
  const grouped = groupNewDirectionsBySegment(data?.newDirections);
  if (grouped.length) {
    return grouped.map((group) => ({
      segment: group.segment,
      from: group.from,
      to: group.to,
      departureDate: group.departure,
      stops: group.stops,
    }));
  }

  const offer = parseChangeSelectedOffer(data);
  if (Array.isArray(offer?.directions) && offer.directions.length) {
    return offer.directions.map((direction, index) => {
      const legs = Array.isArray(direction) ? direction : [];
      const first = legs[0];
      const last = legs[legs.length - 1];
      return {
        segment: String(index + 1),
        from: airportCode(
          first?.departure?.location || first?.departureLocation,
        ),
        to: airportCode(last?.arrival?.location || last?.arrivalLocation),
        departureDate:
          first?.departure?.date || first?.departureDate || "",
      };
    });
  }
  if (offer?.destinations?.length) {
    return offer.destinations.map((dest, index) => ({
      segment: String(index + 1),
      from: airportCode(dest?.from),
      to: airportCode(dest?.to),
      departureDate: dest?.departure?.date || dest?.departureDate || "",
    }));
  }
  return [];
}

/** True when at least one journey O&D differs from the original destinations. */
export function isRouteChangeRequest(data, flightData) {
  if (!data) return false;
  if (
    data?.changeRequest === "PassengerInfo" ||
    data?.changeRequest === "Open"
  ) {
    return false;
  }
  return getRequestedDateChangeLines(data, flightData).some(
    (line) => line.routeChanged,
  );
}

export function getOriginalRouteSummary(data, flightData) {
  return formatRoutePairs(getOriginalRoutePairs(data, flightData));
}

export function getRequestedRouteSummary(data) {
  return formatRoutePairs(getRequestedRoutePairs(data));
}

/**
 * Build readable requested-change lines for the queue / review / detail.
 * One line per journey (grouped by segment). Only journeys whose O&D
 * differs from original destinations[] are marked routeChanged.
 */
export function getRequestedDateChangeLines(data, flightData) {
  const booking =
    flightData ||
    getFlightBookingFromChange(data) ||
    data?.originalItinerary ||
    {};
  const originalPairs = getOriginalRoutePairs(data, booking);
  const journeys = groupNewDirectionsBySegment(data?.newDirections);

  const buildLine = (journey, index) => {
    const oldPair =
      originalPairs.find(
        (pair) => String(pair.segment) === String(journey.segment),
      ) ||
      originalPairs[index] ||
      {};
    const from = airportCode(journey.from);
    const to = airportCode(journey.to);
    const originalFrom = airportCode(oldPair.from);
    const originalTo = airportCode(oldPair.to);
    const route = formatOd(from, to) || `Direction ${index + 1}`;
    const originalRoute = formatOd(originalFrom, originalTo);
    const thisRouteChanged = Boolean(
      originalRoute &&
        route &&
        routePairKey(originalFrom, originalTo) !== routePairKey(from, to),
    );
    const oldDate = oldPair.departureDate || "";
    const newDate = journey.departure || "";
    const oldLabel = formatChangeDate(oldDate) || "-";
    const newLabel = formatChangeDate(newDate) || "-";
    const datesUnchanged =
      oldLabel !== "-" && newLabel !== "-" && oldLabel === newLabel;
    const via =
      Array.isArray(journey.stops) && journey.stops.length
        ? ` via ${journey.stops.join(", ")}`
        : "";

    let line;
    if (thisRouteChanged) {
      line = datesUnchanged
        ? `${originalRoute} → ${route}${via} · ${newLabel}`
        : oldLabel !== "-" || newLabel !== "-"
          ? `${originalRoute} → ${route}${via} · ${oldLabel} → ${newLabel}`
          : `${originalRoute} → ${route}${via}`;
    } else {
      line = `${route}${via}: ${oldLabel} → ${newLabel}`;
    }

    return {
      route,
      from,
      to,
      originalRoute,
      originalFrom,
      originalTo,
      routeChanged: thisRouteChanged,
      oldDate,
      newDate,
      oldLabel,
      newLabel,
      segmentRef: journey.segmentRefs?.[0] || "",
      segmentRefs: journey.segmentRefs || [],
      segment: journey.segment || String(index + 1),
      journeyType: journey.journeyType || "",
      legs: journey.legs?.length || 1,
      stops: journey.stops || [],
      line,
    };
  };

  if (journeys.length) {
    return journeys.map((journey, index) => buildLine(journey, index));
  }

  // Offer-only fallback when newDirections is empty.
  const fallback = getRequestedRoutePairs(data);
  if (!fallback.length) return [];
  return fallback.map((pair, index) =>
    buildLine(
      {
        from: pair.from,
        to: pair.to,
        departure: pair.departureDate,
        segment: pair.segment,
        journeyType: "",
        legs: [],
        stops: pair.stops || [],
        segmentRefs: [],
      },
      index,
    ),
  );
}

export function getRequestedDateChangeSummary(data, flightData) {
  const lines = getRequestedDateChangeLines(data, flightData);
  if (!lines.length) return "";
  return lines.map((item) => item.line).join("; ");
}

function personName(person) {
  return [person?.title, person?.firstName, person?.middleName, person?.lastName]
    .filter(Boolean)
    .join(" ");
}

/** Segment refs selected on this change / open request. */
export function getChangeDirectionRefs(data) {
  return (data?.directionsToChange || [])
    .map((ref) => String(ref))
    .filter(Boolean);
}

export function getItineraryLegsFromChange(data) {
  const raw = data?.originalItinerary;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.flights)) return raw.flights;
  return getFlightBookingFromChange(data)?.flights || [];
}

/**
 * Legs in scope for this request.
 * When directionsToChange is set, only those segmentRefs — not the whole ticket.
 */
export function getScopedItineraryLegs(data) {
  const allLegs = getItineraryLegsFromChange(data);
  const refs = new Set(getChangeDirectionRefs(data));
  if (!refs.size) {
    return { legs: allLegs, allLegs, partial: false };
  }
  const scoped = allLegs.filter((leg) => refs.has(String(leg?.segmentRef)));
  if (!scoped.length) {
    return { legs: allLegs, allLegs, partial: false };
  }
  const allDirections = new Set(
    allLegs.map((leg) => String(leg?.segment)).filter(Boolean),
  );
  const scopedDirections = new Set(
    scoped.map((leg) => String(leg?.segment)).filter(Boolean),
  );
  const partial =
    allDirections.size > 1 && scopedDirections.size < allDirections.size;
  return { legs: scoped, allLegs, partial };
}

function directionTitleForSegment(segmentKey, index) {
  if (segmentKey === "1" || index === 0) return "Outbound";
  if (segmentKey === "2" || index === 1) return "Return";
  return `Segment ${segmentKey}`;
}

/** Table / summary for open-ticket (and other direction-scoped) requests. */
export function getOpenTicketTripSummary(data) {
  const booking = getFlightBookingFromChange(data);
  const { legs, allLegs, partial } = getScopedItineraryLegs(data);
  const routeLegs = legs.length ? legs : allLegs;

  const groups = [];
  for (const leg of routeLegs) {
    const key = String(leg?.segment || groups.length + 1);
    let group = groups.find((item) => item.key === key);
    if (!group) {
      group = { key, legs: [] };
      groups.push(group);
    }
    group.legs.push(leg);
  }

  const firstGroup = groups[0];
  const lastGroup = groups.at(-1) || firstGroup;
  const from = firstGroup?.legs?.[0]?.departureLocation || "";
  const to = lastGroup?.legs?.at(-1)?.arrivalLocation || "";
  const firstFlight = routeLegs[0] || booking?.flights?.at(0) || {};

  let directionLabel = "Open";
  if (groups.length === 1) {
    directionLabel = directionTitleForSegment(firstGroup.key, 0);
  } else if (groups.length > 1) {
    directionLabel = "Both directions";
  }

  const totalDirections = new Set(
    allLegs.map((leg) => String(leg?.segment)).filter(Boolean),
  ).size;

  let flightType = "One Way";
  if (partial && groups.length === 1) {
    flightType = `${directionLabel} only`;
  } else if (groups.length > 1 || totalDirections > 1) {
    flightType = partial ? "Partial open" : "Return";
  }

  const directionNote = partial
    ? `${directionLabel} · ${from} → ${to} (not the whole ticket)`
    : "";

  return {
    booking,
    legs: routeLegs,
    allLegs,
    partial,
    from,
    to,
    firstFlight,
    flightType,
    directionLabel,
    directionNote,
    groups,
  };
}

/** Passengers on this change request — not everyone on the ticket. */
export function getChangeRequestPassengers(data, flightData) {
  const booking = flightData || getFlightBookingFromChange(data) || {};
  const travelers = booking?.travelers || [];
  const info = Array.isArray(data?.passengerInfo) ? data.passengerInfo : [];
  if (info.length) {
    return info
      .map((person) => {
        const name = personName(person);
        const type = person?.travelerType || person?.type || "";
        return type ? `${name} (${type})` : name;
      })
      .filter(Boolean);
  }
  const ids = Array.isArray(data?.passengerIds)
    ? data.passengerIds.map(String)
    : [];
  if (ids.length) {
    return travelers
      .filter((traveler) => ids.includes(String(traveler?.id)))
      .map((person) => {
        const name = personName(person);
        const type = person?.travelerType || person?.type || "";
        return type ? `${name} (${type})` : name;
      })
      .filter(Boolean);
  }
  return travelers
    .map((person) => {
      const name = personName(person);
      const type = person?.travelerType || person?.type || "";
      return type ? `${name} (${type})` : name;
    })
    .filter(Boolean);
}
